<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Pessoas Físicas</h5>
      <b-button
        v-if="hasPermission('adicionar_pessoa_fisica')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-person-plus-fill" title="Novo Cadastro"></i>
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <input
        v-model="filters.nome"
        placeholder="Filtrar por nome"
        class="form-control"
        @input="filtrarPessoas"
      />
      <input
        v-model="filters.cpf"
        placeholder="Filtrar por CPF"
        class="form-control"
        @input="filtrarPessoas"
      />
      <input
        v-model="filters.email"
        placeholder="Filtrar por e-mail"
        class="form-control"
        @input="filtrarPessoas"
      />
      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('id')">
              ID {{ arrow('id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('nome')">
              Nome {{ arrow('nome') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('cpf')">
              CPF {{ arrow('cpf') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('data_nascimento')">
              Data de Nascimento {{ arrow('data_nascimento') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('email')">
              E-mail {{ arrow('email') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in pessoas" :key="row.id">
          <td>{{ row.id }}</td>
          <td>{{ row.nome }}</td>
          <td>{{ formatarCPF(row.cpf) }}</td>
          <td>{{ utils.formatarData(row.data_nascimento) }}</td>
          <td>{{ row.email }}</td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_pessoa_fisica')"
              class="bi bi-eye-fill text-primary mx-2"
              title="Detalhar"
              @click="detalharPessoa(row.id)"
            ></i>
            <i
              v-if="hasPermission('alterar_pessoa_fisica')"
              class="bi bi-pencil-fill text-warning mx-2"
              title="Editar"
              @click="editarPessoa(row.id)"
            ></i>
            <i
              v-if="hasPermission('excluir_pessoa_fisica')"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluirPessoa(row.id)"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="onChangeLimit"
        />
      </div>

      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import { useSortableTable } from '@/utils/useSortableTable'
import globalUtils from '@/utils/globalUtils.js'

const router = useRouter()
const { proxy } = getCurrentInstance()

const {
  items: pessoas,
  totalItems,
  page: paginaAtual,
  limit: limitePorPagina,
  filters,
  load,
  sort,
  arrow
} = useSortableTable(
  `/cadastros/pessoas-fisicas`,
  { key: 'nome', order: 'asc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])

const startRow = computed(() =>
  totalItems.value === 0 ? 0 : (paginaAtual.value - 1) * limitePorPagina.value + 1
)

const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalItems.value)
)

watch(paginaAtual, () => load())
watch(limitePorPagina, () => {
  paginaAtual.value = 1
  load()
})

onMounted(() => {
  filters.value = { nome: '', cpf: '', email: '' }
  load()
})

function filtrarPessoas() {
  paginaAtual.value = 1
  load()
}

function limparFiltros() {
  filters.value.nome = ''
  filters.value.cpf = ''
  filters.value.email = ''
  paginaAtual.value = 1
  load()
}

function onChangeLimit() {
  paginaAtual.value = 1
  load()
}

function onPageChange() {
  load()
}

function irParaCadastro() {
  router.push('/cadastros/pessoas-fisicas/novo')
}

function detalharPessoa(id) {
  router.push(`/cadastros/pessoas-fisicas/${id}/detalhar`)
}

function editarPessoa(id) {
  router.push(`/cadastros/pessoas-fisicas/${id}`)
}

async function excluirPessoa(id) {
  const confirmou = await proxy.$confirm(
    'Tem certeza que deseja excluir esta pessoa?',
    'Confirmação'
  )
  if (!confirmou) return

  try {
    await axios.delete(`/cadastros/pessoas-fisicas/${id}`)
    load()
    proxy.$toast.success('Pessoa física excluída com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.error || 'Erro ao excluir pessoa.'
    await proxy.$alert(msg, 'Erro')
  }
}

function formatarCPF(cpf) {
  if (!cpf) return '—'
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

const utils = globalUtils
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>